import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'te.admin.session';

// withCredentials so the HttpOnly owner cookie set by /admin/login also
// travels with subsequent same-origin requests.
const client = axios.create({ baseURL: API, withCredentials: true });

const authHeaders = () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Admin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [customOption, setCustomOption] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [licSearch, setLicSearch] = useState('');
  const [licResults, setLicResults] = useState([]);
  const [licBusy, setLicBusy] = useState(false);
  const [pwCur, setPwCur] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwNew2, setPwNew2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  // Verify any existing session token against the server on mount.
  // Hiding UI on a client flag alone is not security; server always re-checks.
  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    client.get(`/admin/session`, { headers: authHeaders() })
      .then(() => setAuthed(true))
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); });
  }, []);

  const login = async () => {
    try {
      const r = await client.post(`/admin/login`, { password });
      if (r.data.ok && r.data.token) {
        sessionStorage.setItem(TOKEN_KEY, r.data.token);
        setPassword('');
        setAuthed(true);
        toast.success('Admin unlocked');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) toast.error('Too many attempts. Try again shortly.');
      else toast.error('Invalid credentials');
    }
  };

  const logout = async () => {
    try { await client.post(`/admin/logout`, {}, { headers: authHeaders() }); } catch {}
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    // Send the browser back to the customer activation screen. The HttpOnly
    // owner cookie has been cleared server-side; the license gate will
    // re-render its activation form on the home route.
    navigate('/', { replace: true });
  };

  const enterStudio = () => {
    // Owner has a valid HttpOnly te_owner_session cookie set by /admin/login;
    // navigating to "/" triggers <LicenseGate> which re-checks /api/license/status
    // and unlocks the studio server-side.
    navigate('/', { replace: false });
  };

  const promoteCustom = async () => {
    if (!customCategory || !customOption) return toast.error('Fill both fields');
    try {
      const cur = await client.get(`/admin/options`, { headers: authHeaders() });
      const options = cur.data.options || {};
      if (!options[customCategory]) options[customCategory] = [];
      options[customCategory].push(customOption);
      await client.put(`/admin/options`, { options }, { headers: authHeaders() });
      toast.success('Option added');
      setCustomOption('');
    } catch (e) {
      if (e?.response?.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); toast.error('Session expired'); }
      else toast.error('Save failed');
    }
  };

  const searchLicenses = async () => {
    if (!licSearch || licSearch.length < 2) return toast.error('Enter at least 2 characters');
    setLicBusy(true);
    try {
      const r = await client.get(`/admin/licenses`, { params: { email: licSearch }, headers: authHeaders() });
      setLicResults(r.data.results || []);
      if (!(r.data.results || []).length) toast.info('No licenses match that email prefix.');
    } catch (e) {
      if (e?.response?.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); toast.error('Session expired'); }
      else toast.error('Search failed');
    } finally { setLicBusy(false); }
  };

  const releaseAll = async (licenseHash) => {
    if (!window.confirm('Release ALL authorized devices for this license? The customer will need to enter their license key again on their next device.')) return;
    try {
      await client.post(`/admin/licenses/release-device`, { license_hash: licenseHash }, { headers: authHeaders() });
      toast.success('Devices released');
      await searchLicenses();
    } catch (e) {
      if (e?.response?.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); toast.error('Session expired'); }
      else toast.error('Release failed');
    }
  };

  const changePassword = async () => {
    if (!pwCur || !pwNew) return toast.error('Fill both current and new password');
    if (pwNew.length < 8) return toast.error('New password must be at least 8 characters');
    if (pwNew !== pwNew2) return toast.error('New passwords do not match');
    setPwBusy(true);
    try {
      await client.post(`/admin/change-password`, { current_password: pwCur, new_password: pwNew }, { headers: authHeaders() });
      toast.success('Password changed. Use the new password next time you sign in.');
      setPwCur(''); setPwNew(''); setPwNew2('');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error(e?.response?.data?.detail || 'Current password is incorrect');
      else if (status === 400) toast.error(e?.response?.data?.detail || 'Password did not meet requirements');
      else toast.error('Change failed');
    } finally { setPwBusy(false); }
  };

  return (
    <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10" data-testid="admin-page">
      <div className="eyebrow">Private</div>
      <h1 className="font-display text-5xl mb-6">Owner Admin</h1>

      {!authed ? (
        <div className="rounded-2xl chrome-border bg-zinc-900/60 p-6 space-y-3">
          <p className="text-sm text-zinc-400">Enter the admin password to unlock option management.</p>
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="admin-login" onClick={login} className="btn-magenta rounded-full px-6 py-2 text-xs uppercase tracking-widest">Unlock</button>
          <p className="text-xs text-zinc-500">This area is private and never required for the public studio.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl chrome-border bg-zinc-900/60 p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="eyebrow">Session</div>
              <div className="flex items-center gap-2">
                <button data-testid="admin-enter-studio" onClick={enterStudio} className="btn-magenta rounded-full px-4 py-1.5 text-xs uppercase tracking-widest">Enter Studio</button>
                <button data-testid="admin-logout" onClick={logout} className="chrome-border rounded-full px-4 py-1.5 text-xs uppercase tracking-widest">Sign Out</button>
              </div>
            </div>
            <p className="text-sm text-zinc-300 mt-2">Admin unlocked. Add custom options that appear in the public studio without a redeploy. Use <span className="text-white font-semibold">Enter Studio</span> to open the full generator using your owner session.</p>
          </div>

          <div className="rounded-2xl chrome-border bg-zinc-900/60 p-6 space-y-3">
            <h3 className="font-display text-2xl">Add Custom Option</h3>
            <input data-testid="admin-category" value={customCategory} onChange={(e) => setCustomCategory(e.target.value)} placeholder="Category key (e.g. patterns, materials)" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="admin-option" value={customOption} onChange={(e) => setCustomOption(e.target.value)} placeholder="New option label" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="admin-add-option" onClick={promoteCustom} className="btn-magenta rounded-full px-6 py-2 text-xs uppercase tracking-widest">Add To Library</button>
          </div>

          <div className="rounded-2xl chrome-border bg-zinc-900/60 p-6 space-y-3" data-testid="admin-change-password-card">
            <h3 className="font-display text-2xl">Change Admin Password</h3>
            <p className="text-xs text-zinc-500">Update the password used to sign in to this page. The new password is stored securely (bcrypt) in the database and survives redeploys — no need to touch environment variables.</p>
            <input data-testid="admin-pw-current" type="password" autoComplete="current-password" value={pwCur} onChange={(e) => setPwCur(e.target.value)} placeholder="Current password" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="admin-pw-new" type="password" autoComplete="new-password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="New password (min 8 characters)" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            <input data-testid="admin-pw-new-2" type="password" autoComplete="new-password" value={pwNew2} onChange={(e) => setPwNew2(e.target.value)} placeholder="Confirm new password" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
            <button data-testid="admin-pw-save" onClick={changePassword} disabled={pwBusy} className="btn-magenta rounded-full px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-60">{pwBusy ? 'Saving…' : 'Update Password'}</button>
          </div>

          <div className="rounded-2xl chrome-border bg-zinc-900/60 p-6 space-y-3" data-testid="admin-licenses-card">
            <h3 className="font-display text-2xl">License Devices</h3>
            <p className="text-xs text-zinc-500">Search a customer license by buyer email prefix. Release a device slot if a customer needs to move to a new machine. Public visitors cannot see this.</p>
            <div className="flex gap-2">
              <input data-testid="admin-license-search" value={licSearch} onChange={(e) => setLicSearch(e.target.value)} placeholder="Buyer email prefix (min 2 chars)" className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm" />
              <button data-testid="admin-license-search-btn" onClick={searchLicenses} disabled={licBusy} className="chrome-border rounded-full px-4 py-2 text-xs uppercase tracking-widest">Search</button>
            </div>
            {licResults.length === 0 ? (
              <p className="text-xs text-zinc-600 italic">No results yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="admin-license-results">
                {licResults.map((r) => (
                  <li key={r.license_hash} className="border border-zinc-800 rounded-lg p-3 text-xs space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-zinc-300 font-mono">{r.buyer_email_masked || '—'}</div>
                        <div className="text-zinc-500">{r.active_devices}/{r.total_devices || 0} active devices · product <span className="font-mono">{r.product_link}</span>{r.revoked ? ' · REVOKED' : ''}</div>
                      </div>
                      <button data-testid={`admin-license-release-${r.license_hash.slice(0,8)}`} onClick={() => releaseAll(r.license_hash)} className="chrome-border rounded-full px-3 py-1 text-[10px] uppercase tracking-widest">Release All Devices</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
